import _ from 'lodash'
import { COMPONENT_LIST } from '@/utils/defaultComponentConfig'
import { EMPTY_SCREEN_TEMPLATE } from '@/views/screen/data'
import { randomString } from '@/utils/common'
import { getScreenDataApi } from '@/views/screen/fetch'

/** 菜单 Tab 激活态「标题样式模版」唯一允许的边框图（与标题组件模版列表中该项一致） */
export const COMPOSITE_TAB_BAR_ACTIVE_TEMPLATE_URL =
  'https://kylin-cloud.oss-cn-hangzhou.aliyuncs.com/prod/docx/is-active.svg'

/** 与《组合大屏技术实现文档》一致 */
export const COMPOSITE_COMPONENT_ALLOWLIST = new Set([
  'text',
  'timeClock',
  'richText',
  'borderBlock',
  'imageBlock',
  'compositeTabBar',
  'compositePanelHost',
])

/** 新建即预置、不可删、不可再从物料添加第二套的壳组件 */
export const COMPOSITE_SHELL_TYPES = new Set(['compositeTabBar', 'compositePanelHost'])

export function isCompositeShellComponent (item) {
  return !!(item && COMPOSITE_SHELL_TYPES.has(item.chartComponent))
}

/**
 * 菜单 Tab 条样式（持久化在 chartSet.barStyle）
 * 默认：容器与 Tab 均无底色、Tab 无边框；激活态默认强调文字色与字重，背景透明（无边框配置项）。
 */
export const COMPOSITE_TAB_BAR_BARSTYLE_DEFAULTS = {
  fontSize: 14,
  color: 'rgba(255,255,255,0.9)',
  fontFamily: '',
  fontWeight: '400',
  /** Element UI 取色器对 transparent 解析异常易显成红色，统一用 rgba 透明 */
  barBackground: 'rgba(0, 0, 0, 0)',
  barPadding: '0',
  tabGap: 8,
  tabPadding: '4px 12px',
  tabBorderRadius: 4,
  tabBorder: 'none',
  tabBackground: 'rgba(0, 0, 0, 0)',
  activeColor: '#409EFF',
  activeBackground: 'rgba(0, 0, 0, 0)',
  activeFontWeight: '600',
  /** 激活态：color 为现有颜色+背景；template 为固定 SVG 模版（仅 COMPOSITE_TAB_BAR_ACTIVE_TEMPLATE_URL） */
  activePresentation: 'color',
  /** 与标题「标题样式」模版一致：true 铺满，false 原比例居中 */
  activeTemplateStretch: true,
}

function normalizeColorPickerTransparent (v) {
  if (v == null || v === '') return 'rgba(0, 0, 0, 0)'
  const s = String(v).trim().toLowerCase()
  if (s === 'transparent' || s === 'none') return 'rgba(0, 0, 0, 0)'
  return v
}

/** 合并默认值与已存配置（后者覆盖前者） */
export function mergeCompositeTabBarBarStyle (existing) {
  const merged = {
    ...COMPOSITE_TAB_BAR_BARSTYLE_DEFAULTS,
    ...(existing && typeof existing === 'object' ? existing : {}),
  }
  merged.barBackground = normalizeColorPickerTransparent(merged.barBackground)
  merged.tabBackground = normalizeColorPickerTransparent(merged.tabBackground)
  merged.activeBackground = normalizeColorPickerTransparent(merged.activeBackground)
  const pres = merged.activePresentation === 'template' ? 'template' : 'color'
  merged.activePresentation = pres
  merged.activeTemplateStretch = merged.activeTemplateStretch !== false
  return merged
}

export function normalizePanelType (row) {
  if (!row || row.panelType !== 'composite') return 'normal'
  return 'composite'
}

/** 接口 panelType 大小写/空格差异兼容 */
export function isCompositePanelTypeValue (v) {
  if (v == null || v === '') return false
  return String(v).trim().toLowerCase() === 'composite'
}

/**
 * 嵌套层数限制：仅当「接口声明为组合」且 chartViews 同时具备菜单 Tab + 子屏容器时，才视为组合大屏。
 * 避免子屏接口误标 panelType 却无壳结构时被误拦截、导致子屏空白。
 */
export function isCompositeScreenForNestCap (res) {
  if (!res || typeof res !== 'object') return false
  if (!isCompositePanelTypeValue(res.panelType)) return false
  const v = res.chartViews
  if (!Array.isArray(v) || v.length === 0) return true
  const hasBar = v.some((x) => x && x.chartComponent === 'compositeTabBar')
  const hasHost = v.some((x) => x && x.chartComponent === 'compositePanelHost')
  return hasBar && hasHost
}

/** chartViews 单项是否为子屏容器（兼容 chartComponent / chartComponentType） */
export function chartViewItemIsCompositePanelHost (item) {
  if (!item) return false
  if (item.chartComponent === 'compositePanelHost') return true
  const t = item.chartComponentType
  return t === 'CompositePanelHost' || t === 'compositePanelHost'
}

/**
 * POST /panel 要求 chartViews 每项中：chartSet、chartViewData.chartData、chartViewData.initData 均为 JSON 字符串
 * @param {object[]} chartViews
 * @returns {object[]}
 */
export function mapChartViewsToApiPayloadStrings (chartViews) {
  if (!Array.isArray(chartViews)) return []
  return chartViews.map((raw) => {
    const item = _.cloneDeep(raw)
    if (item.chartSet != null && typeof item.chartSet !== 'string') {
      item.chartSet = JSON.stringify(item.chartSet)
    }
    if (item.chartViewData) {
      const cvd = { ...item.chartViewData }
      if (cvd.chartData != null && typeof cvd.chartData !== 'string') {
        cvd.chartData = JSON.stringify(cvd.chartData)
      }
      if (cvd.initData != null && typeof cvd.initData !== 'string') {
        cvd.initData = JSON.stringify(cvd.initData)
      }
      item.chartViewData = cvd
    }
    return item
  })
}

function parseChartSet (item) {
  if (!item || item.chartSet == null) return {}
  if (typeof item.chartSet === 'string') {
    try {
      return JSON.parse(item.chartSet)
    } catch (e) {
      return {}
    }
  }
  return item.chartSet
}

function defaultChartViewData () {
  return {
    chartData: [],
    initDataFlag: 1,
    initData: [],
    selectedIndicatorList: [],
    showIndicator: false,
  }
}

/** 组合大屏「网页链接」页签：相对子屏容器的内边距（px），控制 iframe 在容器内的留白 */
export const COMPOSITE_TAB_URL_INSET_DEFAULTS = Object.freeze({
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
})

/**
 * @param {object} tab chartSet.tabs 单项
 * @returns {{ top: number, right: number, bottom: number, left: number }}
 */
export function normalizeCompositeTabUrlInset (tab) {
  const d = { top: 0, right: 0, bottom: 0, left: 0 }
  if (!tab || typeof tab !== 'object') return d
  const raw = tab.urlInset
  if (raw && typeof raw === 'object') {
    for (const k of ['top', 'right', 'bottom', 'left']) {
      const n = Number(raw[k])
      d[k] = Number.isFinite(n) && n >= 0 ? Math.min(5000, Math.floor(n)) : 0
    }
  }
  return d
}

export function createCompositeTabBarTemplate (groupId = '') {
  return {
    addSn: 0,
    chartComponentType: 'CompositeTabBar',
    chartComponent: 'compositeTabBar',
    boxTitle: '菜单Tab',
    active: false,
    draggable: true,
    chartX: 60,
    chartY: 20,
    chartWidth: 720,
    chartHeight: 48,
    chartTitle: '菜单Tab',
    chartSet: {
      groupId: groupId || '',
      tabs: [
        {
          tabId: 't1',
          label: '子屏一',
          type: 'panel',
          panelSn: '',
          url: '',
          urlInset: { ...COMPOSITE_TAB_URL_INSET_DEFAULTS },
        },
      ],
      barStyle: { ...COMPOSITE_TAB_BAR_BARSTYLE_DEFAULTS },
      dataSource: '',
    },
    jumpPanelSn: null,
    chartViewData: defaultChartViewData(),
    sort: undefined,
    templateImg: '',
  }
}

export function createCompositePanelHostTemplate (groupId = '') {
  return {
    addSn: 0,
    chartComponentType: 'CompositePanelHost',
    chartComponent: 'compositePanelHost',
    boxTitle: '子屏容器',
    active: false,
    draggable: true,
    chartX: 60,
    chartY: 84,
    chartWidth: 1800,
    chartHeight: 960,
    chartTitle: '子屏容器',
    chartSet: {
      groupId: groupId || '',
      dataSource: '',
    },
    jumpPanelSn: null,
    chartViewData: defaultChartViewData(),
    sort: undefined,
    templateImg: '',
  }
}

/**
 * 左侧物料：菜单 Tab + 子屏容器仅随新建预置一对，不在此列表中重复提供
 */
export function buildCompositeComponentList () {
  const flat = COMPONENT_LIST.map(c => c.components).flat()
  const shell = flat.filter(c => COMPOSITE_COMPONENT_ALLOWLIST.has(c.chartComponent) &&
    c.chartComponent !== 'compositeTabBar' &&
    c.chartComponent !== 'compositePanelHost')
  return [
    {
      categoryName: '壳与素材',
      components: shell.map(c => JSON.parse(JSON.stringify(c))),
    },
  ]
}

/**
 * 新建组合大屏：空白 + 预置一对 Tab/容器（同一 groupId）
 */
export function createEmptyCompositeScreenPayload () {
  const gid = `grp_${randomString()}_${randomString()}`
  const base = _.cloneDeep(EMPTY_SCREEN_TEMPLATE)
  base.panelType = 'composite'
  base.chartViews = mapChartViewsToApiPayloadStrings([
    createCompositeTabBarTemplate(gid),
    createCompositePanelHostTemplate(gid),
  ])
  return base
}

/**
 * 组合大屏仅一对壳：保证菜单 Tab 与子屏容器的 groupId 一致（接口/旧数据缺一或错位时修复，避免 tab 状态串到空 groupId）
 * @param {object[]|undefined} chartViews
 */
export function alignCompositeShellPair (chartViews) {
  if (!Array.isArray(chartViews)) return
  const bars = chartViews.filter((v) => v.chartComponent === 'compositeTabBar' && v.chartSet)
  const hosts = chartViews.filter((v) => v.chartComponent === 'compositePanelHost' && v.chartSet)
  if (bars.length !== 1 || hosts.length !== 1) return
  const bar = bars[0]
  const host = hosts[0]
  const bg = bar.chartSet.groupId
  const hg = host.chartSet.groupId
  if (bg && hg && bg !== hg) {
    host.chartSet.groupId = bg
  } else if (bg && !hg) {
    host.chartSet.groupId = bg
  } else if (hg && !bg) {
    bar.chartSet.groupId = hg
  } else if (!bg && !hg) {
    const gid = `grp_${randomString()}_${randomString()}`
    bar.chartSet.groupId = gid
    host.chartSet.groupId = gid
  }
}

/**
 * @returns {string|null} 错误信息；null 表示通过
 */
export function validateCompositeChartViews (chartViews, parentPanelSn) {
  if (!chartViews || !chartViews.length) {
    return '组合大屏须包含菜单 Tab 与子屏容器'
  }
  const bars = []
  const hosts = []
  for (let i = 0; i < chartViews.length; i++) {
    const item = chartViews[i]
    const comp = item.chartComponent
    if (!COMPOSITE_COMPONENT_ALLOWLIST.has(comp)) {
      return `不允许的组件类型：${comp || '(空)'}`
    }
    if (item.chartSet?.dataSource === 'indicatorLib') {
      return `组合大屏禁止指标库数据源：${item.chartTitle || comp}`
    }
    const cs = parseChartSet(item)
    if (comp === 'compositeTabBar') {
      bars.push({ item, cs, groupId: cs.groupId })
    }
    if (comp === 'compositePanelHost') {
      hosts.push({ item, cs, groupId: cs.groupId })
    }
  }
  if (bars.length !== 1) {
    return bars.length === 0 ? '缺少菜单 Tab 组件' : '组合大屏仅允许一套菜单 Tab，请删除多余的菜单 Tab'
  }
  if (hosts.length !== 1) {
    return hosts.length === 0 ? '缺少子屏容器组件' : '组合大屏仅允许一个子屏容器，请删除多余的子屏容器'
  }
  const b = bars[0]
  const h = hosts[0]
  if (!b.groupId || !h.groupId || b.groupId !== h.groupId) {
    return '菜单 Tab 与子屏容器须配对（同一 groupId）'
  }
  const tabs = b.cs.tabs || []
  if (!tabs.length) {
    return '菜单 Tab 至少须保留一个页签'
  }
  for (const t of tabs) {
    if (t.panelSn == null || t.panelSn === '') continue
    if (String(t.panelSn) === String(parentPanelSn)) {
      return '子屏不能指向当前组合大屏自身'
    }
  }
  return null
}

/** 子屏容器内再加载组合大屏时，画布/预览区展示 copy（与保存规则一致） */
export const COMPOSITE_NEST_RUNTIME_BLOCK_MESSAGE =
  '组合大屏仅支持嵌套一层：子屏内不能再加载组合大屏，请将该页签改为普通大屏。'

/**
 * 一层组合嵌套 + 闭环校验（与保存时 validateCompositeTabTargets 规则一致）
 * @param {string|number} rootPanelSn 当前组合大屏 id
 * @param {Array<string|number>} tabTargetPanelSns 菜单 Tab 各页签的 panelSn（可含空）
 * @returns {Promise<{ ok: true } | { ok: false, message: string }>}
 */
export async function validateCompositeTabOneLevelNesting (rootPanelSn, tabTargetPanelSns) {
  const root =
    rootPanelSn == null || rootPanelSn === '' ? '' : String(rootPanelSn).trim()
  const targets = (tabTargetPanelSns || [])
    .map((t) => (t != null && t !== '' ? String(t).trim() : ''))
    .filter(Boolean)
  if (!targets.length || !root) return { ok: true }

  const cache = new Map()
  const fetchPanel = async (sn) => {
    const key = String(sn)
    if (cache.has(key)) return cache.get(key)
    const res = await getScreenDataApi(key)
    cache.set(key, res)
    return res
  }

  const inStack = new Set()
  const dfs = async (sn, depth) => {
    const key = String(sn)
    if (key === root) {
      return {
        ok: false,
        message: '组合大屏页签存在循环引用（不能指向自身或形成闭环）',
      }
    }
    if (inStack.has(key)) {
      return { ok: false, message: '组合大屏页签存在循环引用（形成闭环）' }
    }
    inStack.add(key)
    try {
      const res = await fetchPanel(key)
      if (res?.panelType !== 'composite') return { ok: true }
      if (depth >= 1) {
        return {
          ok: false,
          message:
            '组合大屏仅支持嵌套一层（组合页签指向的组合大屏，其子屏需为普通大屏）',
        }
      }
      const cv = res?.chartViews || []
      const tabBar = cv.find((v) => v && v.chartComponent === 'compositeTabBar')
      let tabSet = tabBar?.chartSet
      if (typeof tabSet === 'string') {
        try {
          tabSet = JSON.parse(tabSet)
        } catch (e) {
          tabSet = null
        }
      }
      const childTabs = Array.isArray(tabSet?.tabs) ? tabSet.tabs : []
      const childTargets = childTabs
        .map((t) =>
          t && (t.panelSn ?? t.panel_sn) != null && (t.panelSn ?? t.panel_sn) !== ''
            ? String(t.panelSn ?? t.panel_sn)
            : '',
        )
        .filter(Boolean)
      for (const c of childTargets) {
        const cRes = await fetchPanel(c)
        if (cRes?.panelType === 'composite') {
          return {
            ok: false,
            message:
              '组合大屏仅支持嵌套一层（子组合的页签不能再选择组合大屏）',
          }
        }
        if (String(c) === root) {
          return { ok: false, message: '组合大屏页签存在循环引用（形成闭环）' }
        }
      }
      return { ok: true }
    } finally {
      inStack.delete(key)
    }
  }

  for (const sn of targets) {
    const r = await dfs(sn, 0)
    if (!r.ok) return r
  }
  return { ok: true }
}
